import React, { useMemo } from 'react'
import { Dialog, DialogTitle, useTheme } from '@mui/material'
import useMediaQuery from '@mui/material/useMediaQuery'
import IconButton from '@mui/material/IconButton'
import CloseIcon from '@mui/icons-material/Close'
import { useTranslation } from 'react-i18next'
import DialogContent from '@mui/material/DialogContent'
import { UserTranslationFragment } from '@graphql/fragments/__generated__/UserTranslation.generated'
import Grid from '@mui/material/Grid'
import Button from '@mui/material/Button'
import DialogActions from '@mui/material/DialogActions'
import { USER_TRANSLATION_STATUS_MAPPING } from '../../../mappings'
import { Form, Formik } from 'formik'
import * as yup from 'yup'
import FormikTextField from '@components/Formik/FormikTextField'
import Loader from '@components/Loader'
import { TranslationStatusEnum } from '../../../generated/gqlTypes'
import FormikAutocomplete from '@components/Formik/FormikAutocomplete'
import Divider from '@mui/material/Divider'

export interface TranslationFormData {
  currentText: string
  status: TranslationStatusEnum
}

interface Props {
  isOpen: boolean
  isLoading: boolean
  isMainTranslator: boolean

  setIsOpen(val: boolean): void

  translations: UserTranslationFragment[]
  onSubmit(id: string, values: TranslationFormData): Promise<void>
}

const UserTranslationDialogForm: React.FC<Props> = ({
  isOpen,
  isLoading,
  isMainTranslator,
  setIsOpen,
  translations,
  onSubmit,
}) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const smDown = useMediaQuery(theme.breakpoints.down('sm'))
  const schema = useMemo(
    () =>
      yup.object().shape<TranslationFormData>({
        currentText: yup.string().required(t('formErrors.valueIsRequired', 'This value is required')),
        status: yup
          .string<TranslationStatusEnum>()
          .required(t('formErrors.valueIsRequired', 'This value is required')),
      }),
    [t]
  )

  if (!translations.length) return null

  const originalText = translations[0].originalText
  const isHtml = /<\/?[a-z][\s\S]*>/i.test(originalText)

  return (
    <Dialog
      open={isOpen}
      onClose={(event, reason) => {
        if (reason === 'backdropClick') {
          return
        }
        setIsOpen(false)
      }}
      maxWidth='lg'
      fullScreen={smDown}
    >
      <DialogTitle>
        <span className='text-capitalize font-bold'>{t('title.translateTheField', 'Translate the field')}</span>
        <span className='absolute right-10 top-10'>
          <IconButton onClick={() => setIsOpen(false)} title={t('close', 'Close')}>
            <CloseIcon fontSize='inherit' />
          </IconButton>
        </span>
      </DialogTitle>
      <DialogContent className='w-1000 max-w-full' dividers>
        <Loader
          className='spinner spinner--small'
          type='ThreeDots'
          color='#333'
          visible={isLoading}
          height={100}
          width={100}
        />
        <Grid container spacing={2} className='text-base'>
          <Grid item xs={12}>
            <div className='mb-2'>
              <strong>{t('originalText', 'Original Text')}:</strong>
            </div>{' '}
            {isHtml ? (
              <div className='max-h-400 overflow-auto mb-20' dangerouslySetInnerHTML={{ __html: originalText }} />
            ) : (
              <div className='max-h-400 overflow-auto mb-20'>{originalText}</div>
            )}
          </Grid>
        </Grid>
        {translations.map(tr => (
          <Formik<TranslationFormData>
            key={tr.id}
            initialValues={{ currentText: tr.currentText, status: tr.status }}
            validationSchema={schema}
            onSubmit={async values => {
              await onSubmit(tr.id, values)
            }}
            enableReinitialize
          >
            {() => {
              const statusOptions =
                isMainTranslator || tr.status === TranslationStatusEnum.Approved
                  ? [
                      TranslationStatusEnum.NotTranslated,
                      TranslationStatusEnum.PartiallyTranslated,
                      TranslationStatusEnum.FullyTranslated,
                      TranslationStatusEnum.Approved,
                    ]
                  : [
                      TranslationStatusEnum.NotTranslated,
                      TranslationStatusEnum.PartiallyTranslated,
                      TranslationStatusEnum.FullyTranslated,
                    ]
              return (
                <Form>
                  <Grid container spacing={2} className='text-base'>
                    <Grid item xs={12}>
                      <FormikAutocomplete
                        name='status'
                        renderInput={() => null}
                        disableClearable
                        options={statusOptions}
                        getOptionLabel={option =>
                          USER_TRANSLATION_STATUS_MAPPING(
                            t,
                            statusOptions.find(m => m === option)
                          )
                        }
                        isOptionEqualToValue={(option, value) => option === value}
                        textFieldProps={{
                          label: t('formLabels.status', 'Status'),
                        }}
                        disabled={tr.status === TranslationStatusEnum.Approved && !isMainTranslator}
                        isDefaultRenderInput
                      />
                    </Grid>

                    <Grid item xs={12}>
                      {isHtml ? (
                        <FormikTextField
                          name='currentText'
                          label={t('formLabels.currentText', 'Current text')}
                          inputProps={{ style: { resize: 'vertical' } }}
                          rows={10}
                          multiline
                          required
                        />
                      ) : (
                        <FormikTextField
                          name='currentText'
                          label={t('formLabels.currentText', 'Current text')}
                          required
                        />
                      )}
                    </Grid>
                    <Grid item xs={12}>
                      <Button type='submit' variant='contained' fullWidth={smDown}>
                        {t('button.submit', 'Submit')}
                      </Button>
                    </Grid>
                  </Grid>

                  <div className='mt-10 mb-20'>
                    <Divider />
                  </div>
                </Form>
              )
            }}
          </Formik>
        ))}
      </DialogContent>
      <DialogActions>
        <Button variant='outlined' onClick={() => setIsOpen(false)} fullWidth={smDown}>
          {t('button.cancel', 'Cancel')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default UserTranslationDialogForm
